import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Users,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  RefreshCw,
  Eye,
  Pause,
  Play,
  Settings,
  Key,
  Save,
  Edit,
  Trash2,
  Plus,
  X,
  Phone,
  DollarSign,
  Package,
  CreditCard,
  Send,
  Search,
  Filter,
  BarChart3,
  Smartphone
} from 'lucide-react';

// API function
const api = (path, opts={}) => {
  const base = '/api';
  const headers = { ...(opts.headers||{}) };
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...headers }
  });
};

const SMSAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [termiiBalance, setTermiiBalance] = useState(null);

  // Configuration
  const [config, setConfig] = useState({
    apiKey: '',
    apiSecret: '',
    defaultSenderId: 'Revstar',
    costPerSms: 4,
    pricePerCredit: 5,
    bulkDiscount: 10,
    isEnabled: false,
    testMode: true,
    testPhoneNumber: ''
  });
  const [configLoading, setConfigLoading] = useState(false);

  // Pricing packages
  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: '',
    credits: '',
    price: '',
    bonusCredits: '',
    description: '',
    isPopular: false,
    sortOrder: 0
  });

  // Merchant credits
  const [credits, setCredits] = useState([]);
  const [creditsPagination, setCreditsPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [creditsSearch, setCreditsSearch] = useState('');
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ amount: '', type: 'ADMIN_CREDIT', description: '' });

  // SMS Logs
  const [logs, setLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [logsFilter, setLogsFilter] = useState({ status: '', messageType: '' });

  // Test SMS
  const [testSMS, setTestSMS] = useState({ phoneNumber: '', message: '' });
  const [testSMSLoading, setTestSMSLoading] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format number
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const response = await api('/admin/sms/dashboard');
      const data = await response.json();
      if (data.success) {
        setDashboard(data.dashboard);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      const response = await api('/admin/sms/analytics?days=30');
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  // Fetch config
  const fetchConfig = async () => {
    try {
      const response = await api('/admin/sms/config');
      const data = await response.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, ...data.config }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  // Fetch Termii balance
  const fetchTermiiBalance = async () => {
    try {
      const response = await api('/admin/sms/termii-balance');
      const data = await response.json();
      if (data.success) {
        setTermiiBalance(data.balance);
      }
    } catch (error) {
      console.error('Error fetching Termii balance:', error);
    }
  };

  // Fetch pricing packages
  const fetchPackages = async () => {
    try {
      const response = await api('/admin/sms/pricing');
      const data = await response.json();
      if (data.success) {
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  // Fetch merchant credits
  const fetchCredits = async (page = 1) => {
    try {
      const response = await api(`/admin/sms/credits?page=${page}&limit=20&search=${creditsSearch}`);
      const data = await response.json();
      if (data.success) {
        setCredits(data.credits || []);
        setCreditsPagination(data.pagination || { page: 1, limit: 20, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  // Fetch SMS logs
  const fetchLogs = async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(logsFilter.status && { status: logsFilter.status }),
        ...(logsFilter.messageType && { messageType: logsFilter.messageType })
      });
      const response = await api(`/admin/sms/logs?${params}`);
      const data = await response.json();
      if (data.success) {
        setLogs(data.logs || []);
        setLogsPagination(data.pagination || { page: 1, limit: 20, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  // Save config
  const saveConfig = async () => {
    try {
      setConfigLoading(true);
      setError('');
      setSuccess('');

      const response = await api('/admin/sms/config', {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('SMS configuration saved successfully!');
        setConfig(prev => ({ ...prev, ...data.config }));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (error) {
      setError('Failed to save configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  // Save pricing package
  const savePackage = async () => {
    try {
      setError('');
      const url = editingPackage
        ? `/admin/sms/pricing/${editingPackage.id}`
        : '/admin/sms/pricing';
      const method = editingPackage ? 'PUT' : 'POST';

      const response = await api(url, {
        method,
        body: JSON.stringify(packageForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(editingPackage ? 'Package updated!' : 'Package created!');
        setShowPackageModal(false);
        setEditingPackage(null);
        setPackageForm({ name: '', credits: '', price: '', bonusCredits: '', description: '', isPopular: false, sortOrder: 0 });
        fetchPackages();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save package');
      }
    } catch (error) {
      setError('Failed to save package');
    }
  };

  // Delete pricing package
  const deletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const response = await api(`/admin/sms/pricing/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setSuccess('Package deleted!');
        fetchPackages();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to delete package');
      }
    } catch (error) {
      setError('Failed to delete package');
    }
  };

  // Adjust credits
  const adjustCredits = async () => {
    if (!adjustModal || !adjustForm.amount || !adjustForm.description) {
      setError('Please fill all fields');
      return;
    }

    try {
      const response = await api(`/admin/sms/credits/${adjustModal.merchantId}/adjust`, {
        method: 'POST',
        body: JSON.stringify(adjustForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Credits ${adjustForm.type === 'ADMIN_CREDIT' ? 'added' : 'deducted'} successfully!`);
        setAdjustModal(null);
        setAdjustForm({ amount: '', type: 'ADMIN_CREDIT', description: '' });
        fetchCredits(creditsPagination.page);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to adjust credits');
      }
    } catch (error) {
      setError('Failed to adjust credits');
    }
  };

  // Send test SMS
  const sendTestSMS = async () => {
    if (!testSMS.phoneNumber || !testSMS.message) {
      setError('Please enter phone number and message');
      return;
    }

    try {
      setTestSMSLoading(true);
      const response = await api('/admin/sms/test', {
        method: 'POST',
        body: JSON.stringify(testSMS)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message);
        setTestSMS({ phoneNumber: '', message: '' });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to send test SMS');
      }
    } catch (error) {
      setError('Failed to send test SMS');
    } finally {
      setTestSMSLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED':
        return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' };
      case 'SENT':
        return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' };
      case 'PENDING':
        return { bg: '#fef3c7', text: '#d97706', border: '#fed7aa' };
      case 'FAILED':
        return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
    }
  };

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchAnalytics(),
        fetchConfig(),
        fetchTermiiBalance(),
        fetchPackages()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Fetch credits when search changes
  useEffect(() => {
    if (activeTab === 'credits') {
      const debounce = setTimeout(() => fetchCredits(1), 500);
      return () => clearTimeout(debounce);
    }
  }, [creditsSearch, activeTab]);

  // Fetch logs when filters change
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs(1);
    }
  }, [logsFilter, activeTab]);

  const statsCards = [
    {
      title: 'SMS Enabled',
      value: dashboard?.isEnabled ? 'Active' : 'Disabled',
      icon: Activity,
      color: dashboard?.isEnabled ? '#10b981' : '#ef4444'
    },
    {
      title: 'Total Merchants',
      value: formatNumber(dashboard?.totalMerchants),
      icon: Users
    },
    {
      title: 'Credits in Circulation',
      value: formatNumber(dashboard?.totalCreditsInCirculation),
      icon: CreditCard
    },
    {
      title: 'Total SMS Sent',
      value: formatNumber(dashboard?.totalSMSSent),
      icon: MessageSquare
    },
    {
      title: "Today's SMS",
      value: formatNumber(dashboard?.todaySMS),
      icon: Send
    },
    {
      title: 'Pending SMS',
      value: formatNumber(dashboard?.pendingSMS),
      icon: Clock
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Smartphone size={32} color="var(--brand)" />
            <div>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text)' }}>
                SMS Administration
              </h1>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '16px' }}>
                Manage SMS configuration, pricing, credits, and monitor usage
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchDashboard();
              fetchAnalytics();
              fetchConfig();
              fetchTermiiBalance();
            }}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={16} />
            {error}
            <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#166534',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        {/* CRITICAL: Test Mode Warning Banner */}
        {config.testMode && (
          <div style={{
            padding: '16px 20px',
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '12px',
            color: '#92400e',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <AlertTriangle size={24} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: '0 0 4px 0', fontWeight: '700', fontSize: '16px' }}>
                SMS Test Mode is ENABLED
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                <strong>No actual SMS messages are being delivered.</strong> All merchants' SMS notifications
                (order confirmations, shipping updates, etc.) are being logged but NOT sent to customers.
              </p>
              <p style={{ margin: 0, fontSize: '13px' }}>
                Go to <strong>Configuration</strong> tab and disable test mode to enable real SMS delivery.
              </p>
            </div>
            <button
              onClick={() => setActiveTab('config')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Go to Config
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'config', label: 'Configuration', icon: Settings },
            { id: 'pricing', label: 'Pricing Packages', icon: Package },
            { id: 'credits', label: 'Merchant Credits', icon: CreditCard },
            { id: 'logs', label: 'SMS Logs', icon: MessageSquare },
            { id: 'test', label: 'Test SMS', icon: Send }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
              style={{ marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Stats Cards */}
          <div className="cards-4" style={{ marginBottom: '24px' }}>
            {statsCards.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="icon">
                  <stat.icon size={24} color={stat.color || 'var(--brand)'} />
                </div>
                <div className="label">{stat.title}</div>
                <div className="value" style={{ color: stat.color }}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Termii Balance */}
          {termiiBalance && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DollarSign size={18} color="var(--brand)" />
                  <h3 style={{ margin: 0 }}>Termii Account Balance</h3>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Balance</p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text)' }}>
                      {formatCurrency(termiiBalance.balance)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Currency</p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text)' }}>
                      {termiiBalance.currency || 'NGN'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics */}
          {analytics && (
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BarChart3 size={18} color="var(--brand)" />
                  <h3 style={{ margin: 0 }}>30-Day Analytics</h3>
                </div>
              </div>
              <div className="card-body">
                <div className="cards-4">
                  <div className="stat-card">
                    <div className="label">Total Revenue</div>
                    <div className="value">{formatCurrency(analytics.totalRevenue)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="label">Credits Purchased</div>
                    <div className="value">{formatNumber(analytics.totalCreditsPurchased)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="label">Credits Used</div>
                    <div className="value">{formatNumber(analytics.totalCreditsUsed)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="label">Total Transactions</div>
                    <div className="value">{formatNumber(analytics.totalTransactions)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Settings size={18} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Termii SMS Configuration</h3>
            </div>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gap: '20px' }}>
              {/* API Credentials */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>API Key *</label>
                  <input
                    type="password"
                    value={config.apiKey}
                    onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                    placeholder="Enter Termii API key"
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>API Secret</label>
                  <input
                    type="password"
                    value={config.apiSecret}
                    onChange={(e) => setConfig({ ...config, apiSecret: e.target.value })}
                    placeholder="Enter API secret (if required)"
                    className="input"
                  />
                </div>
              </div>

              {/* Sender ID */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Default Sender ID</label>
                <input
                  type="text"
                  value={config.defaultSenderId}
                  onChange={(e) => setConfig({ ...config, defaultSenderId: e.target.value })}
                  placeholder="e.g., Revstar"
                  className="input"
                  maxLength={11}
                />
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                  Max 11 characters, alphanumeric only
                </p>
              </div>

              {/* Pricing */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Cost per SMS (NGN)</label>
                  <input
                    type="number"
                    value={config.costPerSms}
                    onChange={(e) => setConfig({ ...config, costPerSms: parseFloat(e.target.value) })}
                    placeholder="Your Termii cost per SMS"
                    className="input"
                    step="0.01"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Price per Credit (NGN)</label>
                  <input
                    type="number"
                    value={config.pricePerCredit}
                    onChange={(e) => setConfig({ ...config, pricePerCredit: parseFloat(e.target.value) })}
                    placeholder="What merchants pay per credit"
                    className="input"
                    step="0.01"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bulk Discount (%)</label>
                  <input
                    type="number"
                    value={config.bulkDiscount}
                    onChange={(e) => setConfig({ ...config, bulkDiscount: parseFloat(e.target.value) })}
                    placeholder="Discount for bulk purchases"
                    className="input"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.isEnabled}
                    onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Enable SMS Service</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.testMode}
                    onChange={(e) => setConfig({ ...config, testMode: e.target.checked })}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Test Mode (No actual SMS sent)</span>
                </label>
              </div>

              {/* Test Phone */}
              {config.testMode && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Test Phone Number</label>
                  <input
                    type="tel"
                    value={config.testPhoneNumber}
                    onChange={(e) => setConfig({ ...config, testPhoneNumber: e.target.value })}
                    placeholder="Phone number for test SMS (e.g., 2348012345678)"
                    className="input"
                  />
                </div>
              )}

              <button
                onClick={saveConfig}
                disabled={configLoading}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}
              >
                {configLoading ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {configLoading ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Packages Tab */}
      {activeTab === 'pricing' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Package size={18} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>SMS Credit Packages</h3>
            </div>
            <button
              onClick={() => {
                setEditingPackage(null);
                setPackageForm({ name: '', credits: '', bonusCredits: '', description: '', isPopular: false, sortOrder: 0 });
                setShowPackageModal(true);
              }}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Plus size={16} />
              Add Package
            </button>
          </div>
          <div className="card-body">
            {/* Auto-calculation info */}
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <DollarSign size={20} color="#2563eb" />
              <div>
                <p style={{ margin: 0, fontWeight: '600', color: '#1e40af' }}>
                  Prices are auto-calculated
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#3b82f6' }}>
                  Package price = Credits × ₦{config.pricePerCredit}/credit
                  {config.bulkDiscount > 0 && ` (${config.bulkDiscount}% discount for 100+ credits)`}
                </p>
              </div>
            </div>

            {packages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <Package size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0' }}>No Pricing Packages</h3>
                <p style={{ margin: 0 }}>Create your first SMS credit package to get started.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {packages.map(pkg => (
                  <div
                    key={pkg.id}
                    style={{
                      border: pkg.isPopular ? '2px solid var(--brand)' : '1px solid var(--border)',
                      borderRadius: '12px',
                      padding: '20px',
                      position: 'relative',
                      opacity: pkg.isActive ? 1 : 0.6
                    }}
                  >
                    {pkg.isPopular && (
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'var(--brand)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        Most Popular
                      </div>
                    )}
                    <h4 style={{ margin: '0 0 8px 0' }}>{pkg.name}</h4>
                    <p style={{ margin: '0 0 4px 0', fontSize: '32px', fontWeight: '700', color: 'var(--brand)' }}>
                      {formatCurrency(pkg.credits * config.pricePerCredit * (pkg.credits >= 100 && config.bulkDiscount > 0 ? (1 - config.bulkDiscount / 100) : 1))}
                    </p>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {pkg.credits} × ₦{config.pricePerCredit}
                      {pkg.credits >= 100 && config.bulkDiscount > 0 && ` - ${config.bulkDiscount}%`}
                    </p>
                    <p style={{ margin: '0 0 8px 0', color: 'var(--text-muted)' }}>
                      {formatNumber(pkg.credits)} credits
                      {pkg.bonusCredits > 0 && (
                        <span style={{ color: '#10b981', fontWeight: '600' }}> +{pkg.bonusCredits} bonus</span>
                      )}
                    </p>
                    {pkg.description && (
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        {pkg.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setEditingPackage(pkg);
                          setPackageForm({
                            name: pkg.name,
                            credits: pkg.credits,
                            bonusCredits: pkg.bonusCredits || '',
                            description: pkg.description || '',
                            isPopular: pkg.isPopular,
                            sortOrder: pkg.sortOrder || 0
                          });
                          setShowPackageModal(true);
                        }}
                        className="btn btn-secondary"
                        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => deletePackage(pkg.id)}
                        className="btn btn-danger"
                        style={{ padding: '8px 12px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Merchant Credits Tab */}
      {activeTab === 'credits' && (
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CreditCard size={18} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>Merchant Credits</h3>
              </div>
              <div style={{ position: 'relative', width: '300px' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search merchants..."
                  value={creditsSearch}
                  onChange={(e) => setCreditsSearch(e.target.value)}
                  className="input"
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>
          </div>
          <div className="card-body">
            {credits.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0' }}>No Merchant Credits</h3>
                <p style={{ margin: 0 }}>No merchants have purchased SMS credits yet.</p>
              </div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Balance</th>
                      <th>Total Purchased</th>
                      <th>Total Used</th>
                      <th>Last Purchase</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credits.map(credit => (
                      <tr key={credit.id}>
                        <td>
                          <div>
                            <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
                              {credit.merchant?.store?.name || 'Unknown Store'}
                            </p>
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                              {credit.merchant?.email}
                            </p>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight: '600', color: credit.balance > 0 ? '#10b981' : 'var(--text-muted)' }}>
                            {formatNumber(credit.balance)}
                          </span>
                        </td>
                        <td>{formatNumber(credit.totalPurchased)}</td>
                        <td>{formatNumber(credit.totalUsed)}</td>
                        <td>
                          {credit.lastPurchaseAt
                            ? new Date(credit.lastPurchaseAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td>
                          <button
                            onClick={() => setAdjustModal(credit)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '14px' }}
                          >
                            Adjust
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {creditsPagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                {Array.from({ length: creditsPagination.pages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => fetchCredits(i + 1)}
                    className={`btn ${creditsPagination.page === i + 1 ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '8px 12px' }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SMS Logs Tab */}
      {activeTab === 'logs' && (
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MessageSquare size={18} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>SMS Logs</h3>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={logsFilter.status}
                  onChange={(e) => setLogsFilter({ ...logsFilter, status: e.target.value })}
                  className="input"
                  style={{ width: 'auto' }}
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="SENT">Sent</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="FAILED">Failed</option>
                </select>
                <select
                  value={logsFilter.messageType}
                  onChange={(e) => setLogsFilter({ ...logsFilter, messageType: e.target.value })}
                  className="input"
                  style={{ width: 'auto' }}
                >
                  <option value="">All Types</option>
                  <option value="ORDER_NOTIFICATION">Order Notification</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="VERIFICATION">Verification</option>
                  <option value="NOTIFICATION">Notification</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0' }}>No SMS Logs</h3>
                <p style={{ margin: 0 }}>No SMS messages have been sent yet.</p>
              </div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Merchant</th>
                      <th>Recipient</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => {
                      const statusColors = getStatusColor(log.deliveryStatus);
                      return (
                        <tr key={log.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td>
                            <div>
                              <p style={{ margin: 0, fontWeight: '500' }}>
                                {log.merchant?.store?.name || 'Unknown'}
                              </p>
                              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
                                {log.merchant?.email}
                              </p>
                            </div>
                          </td>
                          <td>{log.recipient}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#f3f4f6',
                              fontSize: '12px'
                            }}>
                              {log.messageType}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                              border: `1px solid ${statusColors.border}`,
                              fontSize: '12px'
                            }}>
                              {log.deliveryStatus}
                            </span>
                          </td>
                          <td style={{ maxWidth: '200px' }}>
                            <p style={{
                              margin: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '14px'
                            }}>
                              {log.message}
                            </p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {logsPagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                {Array.from({ length: Math.min(logsPagination.pages, 10) }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => fetchLogs(i + 1)}
                    className={`btn ${logsPagination.page === i + 1 ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '8px 12px' }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test SMS Tab */}
      {activeTab === 'test' && (
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Send size={18} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Send Test SMS</h3>
            </div>
          </div>
          <div className="card-body">
            <div style={{ maxWidth: '500px', display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Phone Number *</label>
                <input
                  type="tel"
                  value={testSMS.phoneNumber}
                  onChange={(e) => setTestSMS({ ...testSMS, phoneNumber: e.target.value })}
                  placeholder="e.g., 2348012345678"
                  className="input"
                />
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                  Include country code (e.g., 234 for Nigeria)
                </p>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Message *</label>
                <textarea
                  value={testSMS.message}
                  onChange={(e) => setTestSMS({ ...testSMS, message: e.target.value })}
                  placeholder="Enter your test message..."
                  className="input"
                  rows={4}
                  maxLength={160}
                />
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                  {testSMS.message.length}/160 characters
                </p>
              </div>
              <button
                onClick={sendTestSMS}
                disabled={testSMSLoading}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}
              >
                {testSMSLoading ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                {testSMSLoading ? 'Sending...' : 'Send Test SMS'}
              </button>
              {config.testMode && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  color: '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertTriangle size={16} />
                  Test mode is enabled. No actual SMS will be sent.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Package Modal */}
      {showPackageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{editingPackage ? 'Edit Package' : 'Create Package'}</h3>
              <button
                onClick={() => setShowPackageModal(false)}
                className="btn btn-secondary"
                style={{ padding: '8px' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Name *</label>
                  <input
                    type="text"
                    value={packageForm.name}
                    onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                    placeholder="e.g., Starter Pack"
                    className="input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Credits *</label>
                    <input
                      type="number"
                      value={packageForm.credits}
                      onChange={(e) => setPackageForm({ ...packageForm, credits: e.target.value })}
                      placeholder="100"
                      className="input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bonus Credits</label>
                    <input
                      type="number"
                      value={packageForm.bonusCredits}
                      onChange={(e) => setPackageForm({ ...packageForm, bonusCredits: e.target.value })}
                      placeholder="0"
                      className="input"
                    />
                  </div>
                </div>

                {/* Auto-calculated price preview */}
                {packageForm.credits && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px'
                  }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#166534' }}>
                      Auto-calculated Price: {formatCurrency(
                        parseInt(packageForm.credits || 0) * config.pricePerCredit *
                        (parseInt(packageForm.credits || 0) >= 100 && config.bulkDiscount > 0
                          ? (1 - config.bulkDiscount / 100)
                          : 1)
                      )}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#15803d' }}>
                      {packageForm.credits} credits × ₦{config.pricePerCredit}/credit
                      {parseInt(packageForm.credits || 0) >= 100 && config.bulkDiscount > 0 &&
                        ` - ${config.bulkDiscount}% bulk discount`}
                    </p>
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description</label>
                  <textarea
                    value={packageForm.description}
                    onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                    placeholder="Optional description..."
                    className="input"
                    rows={2}
                  />
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={packageForm.isPopular}
                      onChange={(e) => setPackageForm({ ...packageForm, isPopular: e.target.checked })}
                    />
                    Mark as Popular
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  <button onClick={savePackage} className="btn btn-primary" style={{ flex: 1 }}>
                    {editingPackage ? 'Update Package' : 'Create Package'}
                  </button>
                  <button onClick={() => setShowPackageModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {adjustModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '90%', maxWidth: '400px' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Adjust Credits</h3>
              <button
                onClick={() => setAdjustModal(null)}
                className="btn btn-secondary"
                style={{ padding: '8px' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="card-body">
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '14px' }}>Merchant</p>
                <p style={{ margin: 0, fontWeight: '500' }}>
                  {adjustModal.merchant?.store?.name || adjustModal.merchant?.email}
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                  Current Balance: <strong>{formatNumber(adjustModal.balance)}</strong> credits
                </p>
              </div>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Type *</label>
                  <select
                    value={adjustForm.type}
                    onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                    className="input"
                  >
                    <option value="ADMIN_CREDIT">Add Credits</option>
                    <option value="ADMIN_DEBIT">Deduct Credits</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Amount *</label>
                  <input
                    type="number"
                    value={adjustForm.amount}
                    onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                    placeholder="Number of credits"
                    className="input"
                    min="1"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Reason *</label>
                  <textarea
                    value={adjustForm.description}
                    onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })}
                    placeholder="Reason for adjustment..."
                    className="input"
                    rows={2}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={adjustCredits} className="btn btn-primary" style={{ flex: 1 }}>
                    Confirm Adjustment
                  </button>
                  <button onClick={() => setAdjustModal(null)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSAdmin;
